/**
 * KeySyncSetting — Settings → Privacy panel for multi-device key sync.
 *
 * Lives inside the Privacy tab. Three states visible to the user:
 *   - No backup yet → "Create sync backup" CTA + inline passphrase form
 *   - Backup exists → "Replace" and "Delete" actions + timestamp
 *   - Busy     → disabled controls + spinner
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  KeyRound,
  RefreshCw,
  Trash2,
  Loader2,
  ShieldCheck,
  Info,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  getKeyBackupStatus,
  uploadKeyBackup,
  deleteKeyBackup,
} from '../utils/keyBackup';

function relTime(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export const KeySyncSetting = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pp, setPp] = useState('');
  const [ppConfirm, setPpConfirm] = useState('');
  const [working, setWorking] = useState(false);

  const refresh = async () => {
    try {
      setLoading(true);
      const s = await getKeyBackupStatus();
      setStatus(s);
    } catch {
      setStatus({ exists: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const submitUpload = async () => {
    if (pp !== ppConfirm) {
      toast.error('Passphrases do not match');
      return;
    }
    if (pp.length < 10) {
      toast.error('Passphrase must be at least 10 characters');
      return;
    }
    setWorking(true);
    try {
      await uploadKeyBackup(pp);
      toast.success(
        status?.exists
          ? 'Sync backup replaced'
          : 'Sync backup created — you can now restore on another device'
      );
      setDialogOpen(false);
      setPp('');
      setPpConfirm('');
      await refresh();
    } catch (e) {
      toast.error(e?.message || 'Backup failed');
    } finally {
      setWorking(false);
    }
  };

  const submitDelete = async () => {
    if (!window.confirm(
      'Delete the sync backup from the server? You will lose the ability to restore onto a new device.'
    )) return;
    setWorking(true);
    try {
      await deleteKeyBackup();
      toast.success('Sync backup deleted');
      await refresh();
    } catch (e) {
      toast.error(e?.message || 'Delete failed');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="key-sync-setting">
      <label className="text-sm text-slate-400">Multi-device key sync</label>
      <div className="p-4 rounded-lg bg-secondary/30 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-900/60 border border-border flex items-center justify-center shrink-0">
            <KeyRound className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Checking backup…
              </div>
            ) : status?.exists ? (
              <>
                <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                  <ShieldCheck className="w-4 h-4" />
                  Sync backup on file
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Encrypted payload uploaded {relTime(status.updated_at)}.
                  On a new device, log in → you'll be offered a "Restore from
                  another device" dialog. The passphrase is needed to
                  decrypt — lose it and this backup becomes useless.
                </p>
              </>
            ) : (
              <>
                <div className="text-sm font-medium text-slate-200">
                  No sync backup
                </div>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Right now this account's private key + trust ledger only
                  exist in this browser. If you wipe / reinstall / switch
                  devices, you lose history (messages within the 24h TTL are
                  still re-readable once you restore).
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button
            size="sm"
            variant={status?.exists ? 'outline' : 'default'}
            onClick={() => setDialogOpen(true)}
            disabled={loading || working}
            data-testid="key-sync-create-replace-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            {status?.exists ? 'Replace backup' : 'Create sync backup'}
          </Button>
          {status?.exists && (
            <Button
              size="sm"
              variant="ghost"
              onClick={submitDelete}
              disabled={working}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              data-testid="key-sync-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Passphrase dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="key-sync-dialog">
          <DialogHeader>
            <DialogTitle>
              {status?.exists ? 'Replace sync backup' : 'Create sync backup'}
            </DialogTitle>
            <DialogDescription className="pt-1 leading-relaxed">
              Choose a passphrase to encrypt this device's private key +
              trust ledger. We upload only the ciphertext — we can't
              decrypt it.{' '}
              <b className="text-red-300">
                Lose this passphrase and the backup is unrecoverable.
              </b>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs text-slate-400 block mb-1.5">
                Sync passphrase (min 10 chars)
              </label>
              <Input
                type="password"
                autoFocus
                value={pp}
                onChange={(e) => setPp(e.target.value)}
                placeholder="Use a phrase, not a word"
                data-testid="key-sync-passphrase-input"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 block mb-1.5">
                Confirm passphrase
              </label>
              <Input
                type="password"
                value={ppConfirm}
                onChange={(e) => setPpConfirm(e.target.value)}
                data-testid="key-sync-passphrase-confirm"
                autoComplete="new-password"
              />
            </div>
            <div className="flex items-start gap-2 text-[11px] text-slate-500 pt-1">
              <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                Not your account password. Pick something a coerced unlock of
                your login wouldn't give up.
              </span>
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              disabled={working}
              data-testid="key-sync-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitUpload}
              disabled={working || !pp || !ppConfirm}
              data-testid="key-sync-submit-btn"
            >
              {working ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
              )}
              {status?.exists ? 'Replace' : 'Create backup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KeySyncSetting;
